import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { httpPost, httpPostFile, useHttpGets } from "../../services/httpService";
import { ApiEndPoints } from "../../utils/endpoints";
import { IFileDetails, IAPIResponse } from "../../interface/files";
import { Status } from "../../utils/constants";

interface IPropertyImageState {
  fetchPropertyImages: {
    data: IFileDetails[];
    error: string;
    status: string;
  };
  deletePropertyImagesResponse: IAPIResponse;
  uploadPropertyImageResponse: IAPIResponse;
}

interface IDeleteFileParams {
  FileName: string;
  WatchlistID: string;
}

interface IUploadFile {
  file: File;
  memberPropertyId: string;
  isAttachment: number;
}

interface IFetchFileParams {
  WatchlistID: string;
  isAttachementData: boolean;
}

const initialState: IPropertyImageState = {
  fetchPropertyImages: {
    data: {} as IFileDetails[],
    error: "",
    status: "",
  },
  uploadPropertyImageResponse: {
    data: {},
    error: "",
    status: "",
  },
  deletePropertyImagesResponse: {
    data: {},
    error: "",
    status: "",
  },
};

export const fetchPropertyImages = createAsyncThunk(
  "propertyImages/fetchPropertyImages",
  async (params: IFetchFileParams, { rejectWithValue }) => {
    try {
      const result = await useHttpGets(`${ApiEndPoints.files}/AllFiles/${params.WatchlistID}`);
      if (result.status === 200) {
        return result.data?.PropertyImages?.Files || [];
      } else {
        return rejectWithValue(result.error);
      }
    } catch (ex: any) {
      return rejectWithValue(ex.message);
    }
  }
);

export const uploadPropertyImageFile = createAsyncThunk(
  "propertyImages/uploadPropertyImageFile",
  async (file: IUploadFile, { rejectWithValue }) => {
    try {
      const result = await httpPostFile(`${ApiEndPoints.files}Uploads/HtmlClient/`, file);
      if (result.status === 200) {
        return file.file;
      } else {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(result.error || "", "text/xml");
        const message = xmlDoc.documentElement.textContent;
        return rejectWithValue(message);
      }
    } catch (ex: any) {
      return rejectWithValue(ex.message);
    }
  }
);

export const deletePropertyImages = createAsyncThunk(
  "propertyImages/deletePropertyImages",
  async (files: IDeleteFileParams, { rejectWithValue }) => {
    const fileNames = [files.FileName];
    try {
      const response = await httpPost(`${ApiEndPoints.files}DeleteFiles`, {
        MemberPropertyId: `${files.WatchlistID}`,
        FileNames: fileNames,
      });
      if (response.error == null) {
        return files.FileName;
      } else {
        return rejectWithValue(response.error);
      }
    } catch (ex: any) {
      return rejectWithValue(ex.message);
    }
  }
);

const propertyImageSlice = createSlice({
  name: "propertyImages",
  initialState,
  reducers: {
    resetFileUploadState: (state) => {
      state.uploadPropertyImageResponse = { data: {}, error: "", status: "" };
    },
    resteDeleteFileState: (state) => {
      state.deletePropertyImagesResponse = { data: {}, error: "", status: "" };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPropertyImages.fulfilled, (state, action) => {
        state.fetchPropertyImages.data = action.payload;
        state.fetchPropertyImages.status = Status.SUCCESS;
        state.fetchPropertyImages.error = "";
      })
      .addCase(fetchPropertyImages.pending, (state) => {
        state.fetchPropertyImages.status = Status.LOADING;
        state.fetchPropertyImages.error = "";
      })
      .addCase(fetchPropertyImages.rejected, (state, action) => {
        state.fetchPropertyImages.status = Status.FAILED;
        state.fetchPropertyImages.error = action.payload as string;
      })
      .addCase(uploadPropertyImageFile.fulfilled, (state, action) => {
        state.uploadPropertyImageResponse.data = action.payload;
        state.uploadPropertyImageResponse.status = Status.SUCCESS;
        state.uploadPropertyImageResponse.error = "";
      })
      .addCase(uploadPropertyImageFile.pending, (state) => {
        state.uploadPropertyImageResponse.status = Status.LOADING;
        state.uploadPropertyImageResponse.error = "";
      })
      .addCase(uploadPropertyImageFile.rejected, (state, action) => {
        state.uploadPropertyImageResponse.status = Status.FAILED;
        state.uploadPropertyImageResponse.error = action.payload as string;
      })
      .addCase(deletePropertyImages.fulfilled, (state, action) => {
        state.deletePropertyImagesResponse.data = action.payload;
        state.deletePropertyImagesResponse.status = Status.SUCCESS;
        state.deletePropertyImagesResponse.error = "";
      })
      .addCase(deletePropertyImages.pending, (state) => {
        state.deletePropertyImagesResponse.status = Status.LOADING;
        state.deletePropertyImagesResponse.error = "";
      })
      .addCase(deletePropertyImages.rejected, (state, action) => {
        state.deletePropertyImagesResponse.status = Status.FAILED;
        state.deletePropertyImagesResponse.error = action.payload as string;
      });
  },
});

export const { resteDeleteFileState, resetFileUploadState } = propertyImageSlice.actions;

export default propertyImageSlice.reducer;
